import React, { useState, useRef } from 'react';
import {
  Box, Container, VStack, HStack, Text, Heading, Button,
  FormControl, FormLabel, Input, Select, Textarea, Divider,
  useToast, Alert, AlertIcon, Badge, Icon, Flex, SimpleGrid,
  Card, CardBody, List, ListItem, ListIcon, Stepper, Step,
  StepIndicator, StepStatus, StepIcon, StepNumber, StepSeparator,
  StepTitle, useSteps,
} from '@chakra-ui/react';
import {
  FaCheck, FaBuilding, FaFileContract, FaHandshake,
  FaShieldAlt, FaUserMd, FaPhone, FaEnvelope, FaMapMarkerAlt,
} from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import { supabase } from '../supabaseClient';

const steps = [
  { title: 'Facility Info' },
  { title: 'Agreement' },
  { title: 'Sign' },
];

export default function FacilitySignup() {
  const { activeStep, setActiveStep } = useSteps({ index: 0, count: steps.length });
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [form, setForm] = useState({
    name: '', address: '', city: '', state: '', zip: '',
    contact_name: '', contact_email: '', contact_phone: '', contact_title: '',
    facility_type: 'nursing_center', notes: '',
  });
  const sigCanvas = useRef(null);
  const toast = useToast();

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const validateStep1 = () => {
    if (!form.name.trim()) { toast({ title: 'Facility name is required', status: 'warning', duration: 3000 }); return false; }
    if (!form.contact_name.trim()) { toast({ title: 'Contact name is required', status: 'warning', duration: 3000 }); return false; }
    if (!form.contact_email.trim()) { toast({ title: 'Contact email is required', status: 'warning', duration: 3000 }); return false; }
    return true;
  };

  const generatePDF = (signatureDataURL) => {
    const doc = new jsPDF();
    const lm = 20;
    const pw = 170;
    let y = 20;

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('AtHomeBloodDraw.com', 105, y, { align: 'center' });
    y += 8;
    doc.setFontSize(13);
    doc.text('Facility Services Agreement', 105, y, { align: 'center' });
    y += 10;
    doc.setDrawColor(0, 128, 128);
    doc.setLineWidth(0.5);
    doc.line(lm, y, lm + pw, y);
    y += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Facility Information:', lm, y); y += 6;
    doc.setFont('helvetica', 'normal');
    doc.text(`Facility: ${form.name}`, lm, y); y += 5;
    if (form.address) { doc.text(`Address: ${form.address}`, lm, y); y += 5; }
    if (form.city) { doc.text(`City/State: ${form.city}, ${form.state || ''} ${form.zip || ''}`, lm, y); y += 5; }
    doc.text(`Contact: ${form.contact_name}`, lm, y); y += 5;
    if (form.contact_title) { doc.text(`Title: ${form.contact_title}`, lm, y); y += 5; }
    doc.text(`Email: ${form.contact_email}`, lm, y); y += 5;
    if (form.contact_phone) { doc.text(`Phone: ${form.contact_phone}`, lm, y); y += 5; }
    y += 5;

    const sections = [
      { title: '1. Services', text: 'AtHomeBloodDraw.com ("Provider") agrees to provide mobile phlebotomy services at the Facility. Services include specimen collection, packaging, and coordination of laboratory shipment for patients/residents at the Facility. Provider will supply all necessary equipment and trained, certified phlebotomists.' },
      { title: '2. Scheduling & Access', text: 'Provider and Facility will coordinate scheduling of blood draw visits. Facility agrees to provide reasonable access to patients/residents, appropriate workspace, and timely communication of scheduling needs. Provider will make best efforts to accommodate scheduling requests.' },
      { title: '3. Compliance & HIPAA', text: 'Both parties agree to comply with all applicable federal, state, and local laws, including HIPAA, the Anti-Kickback Statute, and the Stark Law. Provider maintains a Business Associate Agreement (BAA) and will handle all Protected Health Information (PHI) in accordance with HIPAA regulations. All phlebotomists are trained in HIPAA compliance and universal precautions.' },
      { title: '4. Insurance & Liability', text: 'Provider maintains professional liability insurance with minimum coverage of $1,000,000 per occurrence and $3,000,000 aggregate. Provider carries workers compensation insurance for all phlebotomists. Facility is not liable for injuries sustained by Provider personnel during service delivery.' },
      { title: '5. Billing & Compensation', text: 'Billing arrangements will be agreed upon separately and documented in writing. Standard billing may include per-draw fees, monthly service fees, or as otherwise negotiated. Payment terms are Net 30 days from invoice date unless otherwise agreed.' },
      { title: '6. Term & Termination', text: 'This Agreement is effective upon signing and continues until terminated by either party with thirty (30) days written notice. Either party may terminate immediately for cause, including material breach, loss of licensure, or violation of applicable laws.' },
      { title: '7. Confidentiality', text: 'Both parties agree to maintain the confidentiality of proprietary business information, patient information, and the terms of this Agreement. This obligation survives termination of the Agreement.' },
    ];

    sections.forEach(section => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.text(section.title, lm, y); y += 6;
      doc.setFont('helvetica', 'normal');
      const lines = doc.splitTextToSize(section.text, pw);
      doc.text(lines, lm, y);
      y += lines.length * 5 + 6;
    });

    if (y > 220) { doc.addPage(); y = 20; }
    y += 5;
    doc.line(lm, y, lm + pw, y); y += 8;
    doc.setFont('helvetica', 'bold');
    doc.text('Authorized Signature:', lm, y); y += 8;
    if (signatureDataURL) { doc.addImage(signatureDataURL, 'PNG', lm, y, 60, 20); y += 22; }
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${form.contact_name}`, lm, y); y += 5;
    if (form.contact_title) { doc.text(`Title: ${form.contact_title}`, lm, y); y += 5; }
    doc.text(`Email: ${form.contact_email}`, lm, y); y += 5;
    doc.text(`Date: ${new Date().toLocaleDateString('en-US')}`, lm, y); y += 5;
    doc.text(`Facility: ${form.name}`, lm, y);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('AtHomeBloodDraw.com - Facility Services Agreement', lm, 285);
    return doc;
  };

  const handleSubmit = async () => {
    if (sigCanvas.current?.isEmpty()) {
      toast({ title: 'Please provide your signature', status: 'warning', duration: 3000 });
      return;
    }

    setSigning(true);
    try {
      const canvas = sigCanvas.current.getCanvas();
      const signatureDataURL = canvas.toDataURL('image/png');

      // Create facility record
      const { data: newFacility, error: insertErr } = await supabase
        .from('facilities')
        .insert({
          name: form.name.trim(),
          address: form.address.trim() || null,
          city: form.city.trim() || null,
          state: form.state.trim() || null,
          zip: form.zip.trim() || null,
          contact_name: form.contact_name.trim(),
          contact_email: form.contact_email.trim(),
          contact_phone: form.contact_phone.trim() || null,
          contact_title: form.contact_title.trim() || null,
          facility_type: form.facility_type,
          notes: form.notes.trim() || null,
          agreement_signed: true,
          agreement_signed_at: new Date().toISOString(),
          agreement_signer_name: form.contact_name.trim(),
          agreement_signer_title: form.contact_title.trim() || null,
          agreement_signer_email: form.contact_email.trim(),
        })
        .select('id')
        .single();
      if (insertErr) throw insertErr;

      // Upload signature
      const sigBlob = await (await fetch(signatureDataURL)).blob();
      const sigFileName = `facility-signature-${newFacility.id}-${Date.now()}.png`;
      const { error: sigUploadErr } = await supabase.storage
        .from('doctor-signatures')
        .upload(sigFileName, sigBlob, { contentType: 'image/png', upsert: true });
      if (sigUploadErr) console.error('Signature upload error:', sigUploadErr);

      const { data: sigUrlData } = supabase.storage.from('doctor-signatures').getPublicUrl(sigFileName);

      // Generate and upload PDF
      const pdfDoc = generatePDF(signatureDataURL);
      const pdfBlob = pdfDoc.output('blob');
      const pdfFileName = `facility-agreement-${newFacility.id}-${Date.now()}.pdf`;
      const { error: pdfUploadErr } = await supabase.storage
        .from('doctor-signatures')
        .upload(pdfFileName, pdfBlob, { contentType: 'application/pdf', upsert: true });
      if (pdfUploadErr) console.error('PDF upload error:', pdfUploadErr);

      const { data: pdfUrlData } = supabase.storage.from('doctor-signatures').getPublicUrl(pdfFileName);

      // Update facility with file URLs
      await supabase.from('facilities').update({
        agreement_signature_url: sigUrlData?.publicUrl || null,
        agreement_pdf_url: pdfUrlData?.publicUrl || null,
      }).eq('id', newFacility.id);

      // Download PDF
      pdfDoc.save(`Facility-Agreement-${form.name.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);

      // Log activity
      try {
        await supabase.from('admin_activity_log').insert({
          action: 'New facility signup',
          username: 'System',
          details: `${form.name} signed up and signed agreement — Contact: ${form.contact_name} (${form.contact_email})`,
          created_at: new Date().toISOString(),
        });
      } catch (_) {}

      setSigned(true);
      toast({ title: 'Welcome aboard!', description: 'Your agreement has been signed. A PDF copy has been downloaded.', status: 'success', duration: 5000 });
    } catch (err) {
      toast({ title: 'Error', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setSigning(false);
    }
  };

  // Success page
  if (signed) {
    return (
      <Box minH="100vh" bg="gray.50">
        <Box bgGradient="linear(to-r, teal.600, teal.800)" py={10} color="white" textAlign="center">
          <Icon as={FaBuilding} boxSize={10} mb={3} opacity={0.8} />
          <Heading size="lg">AtHomeBloodDraw.com</Heading>
          <Text opacity={0.9}>Facility Services Agreement</Text>
        </Box>
        <Container maxW="600px" py={12}>
          <Box bg="white" borderRadius="2xl" boxShadow="xl" p={10} textAlign="center">
            <Icon as={FaCheck} boxSize={16} color="green.500" mb={4} p={3} bg="green.50" borderRadius="full" />
            <Heading size="lg" color="gray.800" mb={3}>Welcome Aboard!</Heading>
            <Text color="gray.600" mb={6}>
              Thank you, {form.name}! Your facility has been registered and the agreement has been signed.
              Our team will be in touch shortly to coordinate scheduling and get started.
            </Text>
            <Badge colorScheme="green" fontSize="md" px={4} py={2} borderRadius="full">Signed & Registered</Badge>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Box bgGradient="linear(to-r, teal.600, teal.800)" py={10} color="white" textAlign="center">
        <Icon as={FaBuilding} boxSize={10} mb={3} opacity={0.8} />
        <Heading size="lg">AtHomeBloodDraw.com</Heading>
        <Text opacity={0.9} mt={1}>Facility Partnership Signup</Text>
        <Text opacity={0.7} fontSize="sm" mt={1}>Register your facility and sign the services agreement</Text>
      </Box>

      <Container maxW="800px" py={8} px={4}>
        {/* Stepper */}
        <Box bg="white" borderRadius="xl" boxShadow="sm" p={4} mb={6}>
          <Stepper index={activeStep} colorScheme="teal" size="sm">
            {steps.map((step, i) => (
              <Step key={i}>
                <StepIndicator>
                  <StepStatus complete={<StepIcon />} incomplete={<StepNumber />} active={<StepNumber />} />
                </StepIndicator>
                <Box flexShrink={0}><StepTitle>{step.title}</StepTitle></Box>
                <StepSeparator />
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step 1: Facility Info */}
        {activeStep === 0 && (
          <Box bg="white" borderRadius="xl" boxShadow="md" p={6}>
            <HStack spacing={3} mb={5}>
              <Icon as={FaBuilding} color="teal.500" boxSize={5} />
              <Heading size="md" color="gray.800">Facility Information</Heading>
            </HStack>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="600">Facility Name</FormLabel>
                <Input name="name" value={form.name} onChange={handleChange} placeholder="Sunrise Senior Living" />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">Facility Type</FormLabel>
                <Select name="facility_type" value={form.facility_type} onChange={handleChange}>
                  <option value="nursing_center">Nursing Center</option>
                  <option value="assisted_living">Assisted Living</option>
                  <option value="senior_living">Senior Living</option>
                  <option value="rehabilitation">Rehabilitation</option>
                  <option value="hospice">Hospice</option>
                  <option value="clinic">Clinic</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">Address</FormLabel>
                <Input name="address" value={form.address} onChange={handleChange} placeholder="123 Main St" />
              </FormControl>
              <HStack spacing={4} w="full">
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">City</FormLabel>
                  <Input name="city" value={form.city} onChange={handleChange} placeholder="Brooklyn" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">State</FormLabel>
                  <Input name="state" value={form.state} onChange={handleChange} placeholder="NY" maxW="100px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">ZIP</FormLabel>
                  <Input name="zip" value={form.zip} onChange={handleChange} placeholder="11224" maxW="120px" />
                </FormControl>
              </HStack>

              <Divider />
              <Heading size="sm" color="gray.700" alignSelf="start">Contact Person</Heading>

              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600">Full Name</FormLabel>
                  <Input name="contact_name" value={form.contact_name} onChange={handleChange} placeholder="John Doe" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">Title</FormLabel>
                  <Input name="contact_title" value={form.contact_title} onChange={handleChange} placeholder="Administrator" />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="600">Email</FormLabel>
                  <Input name="contact_email" type="email" value={form.contact_email} onChange={handleChange} placeholder="johndoe@email.com" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">Phone</FormLabel>
                  <Input name="contact_phone" value={form.contact_phone} onChange={handleChange} placeholder="(555) 123-4567" />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">Notes <Text as="span" color="gray.400" fontWeight="400">(optional)</Text></FormLabel>
                <Textarea name="notes" value={form.notes} onChange={handleChange} placeholder="Any additional information..." rows={2} />
              </FormControl>

              <Button colorScheme="teal" size="lg" w="full" py={7} borderRadius="xl" fontWeight="700"
                onClick={() => { if (validateStep1()) setActiveStep(1); }}>
                Continue to Agreement
              </Button>
            </VStack>
          </Box>
        )}

        {/* Step 2: Agreement Terms */}
        {activeStep === 1 && (
          <Box bg="white" borderRadius="xl" boxShadow="md" p={6}>
            <HStack spacing={3} mb={5}>
              <Icon as={FaFileContract} color="teal.500" boxSize={5} />
              <Heading size="md" color="gray.800">Services Agreement</Heading>
            </HStack>

            <Box bg="gray.50" borderRadius="lg" p={5} mb={6} maxH="400px" overflowY="auto">
              <VStack align="start" spacing={4} fontSize="sm" color="gray.700">
                <Box>
                  <Text fontWeight="700" mb={1}>1. Services</Text>
                  <Text>AtHomeBloodDraw.com ("Provider") agrees to provide mobile phlebotomy services at the Facility. Services include specimen collection, packaging, and coordination of laboratory shipment for patients/residents at the Facility. Provider will supply all necessary equipment and trained, certified phlebotomists.</Text>
                </Box>
                <Box>
                  <Text fontWeight="700" mb={1}>2. Scheduling & Access</Text>
                  <Text>Provider and Facility will coordinate scheduling of blood draw visits. Facility agrees to provide reasonable access to patients/residents, appropriate workspace, and timely communication of scheduling needs.</Text>
                </Box>
                <Box>
                  <Text fontWeight="700" mb={1}>3. Compliance & HIPAA</Text>
                  <Text>Both parties agree to comply with all applicable federal, state, and local laws, including HIPAA, the Anti-Kickback Statute, and the Stark Law. Provider maintains a Business Associate Agreement (BAA) and will handle all Protected Health Information (PHI) in accordance with HIPAA regulations.</Text>
                </Box>
                <Box>
                  <Text fontWeight="700" mb={1}>4. Insurance & Liability</Text>
                  <Text>Provider maintains professional liability insurance with minimum coverage of $1,000,000 per occurrence and $3,000,000 aggregate. Provider carries workers compensation insurance for all phlebotomists.</Text>
                </Box>
                <Box>
                  <Text fontWeight="700" mb={1}>5. Billing & Compensation</Text>
                  <Text>Billing arrangements will be agreed upon separately and documented in writing. Standard billing may include per-draw fees, monthly service fees, or as otherwise negotiated. Payment terms are Net 30 days from invoice date.</Text>
                </Box>
                <Box>
                  <Text fontWeight="700" mb={1}>6. Term & Termination</Text>
                  <Text>This Agreement is effective upon signing and continues until terminated by either party with thirty (30) days written notice. Either party may terminate immediately for cause.</Text>
                </Box>
                <Box>
                  <Text fontWeight="700" mb={1}>7. Confidentiality</Text>
                  <Text>Both parties agree to maintain the confidentiality of proprietary business information, patient information, and the terms of this Agreement.</Text>
                </Box>
              </VStack>
            </Box>

            <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
              <AlertIcon />
              Please review the agreement above. By proceeding, you confirm you have read and understand the terms.
            </Alert>

            <HStack spacing={4}>
              <Button variant="outline" size="lg" flex={1} onClick={() => setActiveStep(0)}>Back</Button>
              <Button colorScheme="teal" size="lg" flex={2} py={7} borderRadius="xl" fontWeight="700"
                onClick={() => setActiveStep(2)}>
                I Agree — Proceed to Sign
              </Button>
            </HStack>
          </Box>
        )}

        {/* Step 3: Sign */}
        {activeStep === 2 && (
          <Box bg="white" borderRadius="xl" boxShadow="md" p={6}>
            <HStack spacing={3} mb={5}>
              <Icon as={FaHandshake} color="teal.500" boxSize={5} />
              <Heading size="md" color="gray.800">Sign Agreement</Heading>
            </HStack>

            {/* Summary */}
            <Box bg="teal.50" borderRadius="lg" p={4} mb={5} border="1px" borderColor="teal.100">
              <Text fontSize="sm" fontWeight="700" color="teal.700" mb={2}>Signing on behalf of:</Text>
              <Text fontSize="sm" color="teal.600">{form.name}</Text>
              <Text fontSize="sm" color="teal.600">{form.contact_name}{form.contact_title ? `, ${form.contact_title}` : ''}</Text>
              <Text fontSize="sm" color="teal.600">{form.contact_email}</Text>
            </Box>

            <FormControl isRequired mb={4}>
              <FormLabel fontSize="sm" fontWeight="600">Digital Signature</FormLabel>
              <Text fontSize="xs" color="gray.500" mb={2}>Draw your signature below using your mouse or touchscreen</Text>
              <Box border="2px" borderColor="gray.200" borderRadius="xl" bg="white" overflow="hidden">
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{ width: 700, height: 150, className: 'signature-canvas', style: { width: '100%', height: '150px' } }}
                />
              </Box>
              <Flex justify="flex-end" mt={2}>
                <Button size="xs" variant="ghost" colorScheme="red" onClick={() => sigCanvas.current?.clear()}>Clear Signature</Button>
              </Flex>
            </FormControl>

            <Alert status="info" borderRadius="md" mb={4} fontSize="sm">
              <AlertIcon />
              By signing, you agree to the Facility Services Agreement terms on behalf of {form.name}. A PDF copy will be downloaded for your records.
            </Alert>

            <HStack spacing={4}>
              <Button variant="outline" size="lg" flex={1} onClick={() => setActiveStep(1)}>Back</Button>
              <Button colorScheme="teal" size="lg" flex={2} py={7} borderRadius="xl" fontWeight="700"
                onClick={handleSubmit} isLoading={signing} loadingText="Signing & Registering...">
                Sign & Complete Registration
              </Button>
            </HStack>
          </Box>
        )}

        <Text fontSize="xs" color="gray.400" textAlign="center" mt={8}>
          AtHomeBloodDraw.com — Facility Partnership Signup
        </Text>
      </Container>
    </Box>
  );
}
